import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Paperclip, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["IT Infrastructure", "Networking", "Wi-Fi", "CCTV / Surveillance", "Access Control", "Communication / EPABX", "Automation", "UPS / Power", "AMC / Maintenance", "Other"];
const PRIORITIES = ["Normal", "High — business affected", "Critical — system down"];

export const ServiceRequestForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", site_location: "", service_category: "", system: "", description: "", priority: "Normal", preferred_date: "" });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const label = "block text-xs tracking-[0.18em] uppercase text-slate-400 mb-2";
  const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Attachment must be smaller than 10 MB."); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = new FormData();
      Object.entries(form).forEach(([k, v]) => payload.append(k, v));
      if (file) payload.append("attachment", file);
      await axios.post(`${API}/service-requests`, payload, { headers: { "Content-Type": "multipart/form-data" } });
      navigate("/thank-you?type=service-request");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please call or email support@nexsecure.in");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="service-request-form">
      <div><label htmlFor="sr-name" className={label}>Name *</label><input id="sr-name" data-testid="sr-name" required className={input} value={form.name} onChange={set("name")} placeholder="Your name" /></div>
      <div><label htmlFor="sr-company" className={label}>Company</label><input id="sr-company" data-testid="sr-company" className={input} value={form.company} onChange={set("company")} placeholder="Organisation" /></div>
      <div><label htmlFor="sr-email" className={label}>Email *</label><input id="sr-email" data-testid="sr-email" required type="email" className={input} value={form.email} onChange={set("email")} placeholder="you@company.com" /></div>
      <div><label htmlFor="sr-phone" className={label}>Phone *</label><input id="sr-phone" data-testid="sr-phone" required type="tel" className={input} value={form.phone} onChange={set("phone")} placeholder="+91" /></div>
      <div><label htmlFor="sr-site" className={label}>Site / Location *</label><input id="sr-site" data-testid="sr-site" required className={input} value={form.site_location} onChange={set("site_location")} placeholder="Site address or area" /></div>
      <div>
        <label htmlFor="sr-cat" className={label}>Service Category *</label>
        <select id="sr-cat" data-testid="sr-category" required className={input} value={form.service_category} onChange={set("service_category")}>
          <option value="" disabled>Select category</option>
          {CATEGORIES.map((c) => <option key={c} value={c} className="text-neutral-900">{c}</option>)}
        </select>
      </div>
      <div><label htmlFor="sr-system" className={label}>Equipment / System</label><input id="sr-system" data-testid="sr-system" className={input} value={form.system} onChange={set("system")} placeholder="e.g. NVR, firewall, UPS model" /></div>
      <div>
        <label htmlFor="sr-priority" className={label}>Priority</label>
        <select id="sr-priority" data-testid="sr-priority" className={input} value={form.priority} onChange={set("priority")}>
          {PRIORITIES.map((p) => <option key={p} value={p} className="text-neutral-900">{p}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2"><label htmlFor="sr-desc" className={label}>Problem Description *</label><textarea id="sr-desc" data-testid="sr-description" required rows={4} className={input} value={form.description} onChange={set("description")} placeholder="What happened, since when, what you've tried" /></div>
      <div><label htmlFor="sr-date" className={label}>Preferred Visit Date</label><input id="sr-date" data-testid="sr-date" type="date" className={input} value={form.preferred_date} onChange={set("preferred_date")} /></div>
      <div>
        <label className={label}>Photo / Attachment (optional)</label>
        {file ? (
          <div className="flex items-center justify-between border border-nx-gold/40 bg-nx-gold/[0.06] px-4 py-3" data-testid="sr-file-attached">
            <span className="flex items-center gap-3 text-sm text-slate-200 truncate"><Paperclip size={15} className="text-nx-goldlight shrink-0" /> {file.name}</span>
            <button type="button" onClick={() => setFile(null)} className="text-slate-400 hover:text-white p-1" aria-label="Remove attachment" data-testid="sr-file-remove"><X size={16} /></button>
          </div>
        ) : (
          <label htmlFor="sr-file" data-testid="sr-file-upload" className="flex items-center justify-center gap-3 border border-dashed border-white/20 hover:border-nx-gold/50 text-slate-400 hover:text-slate-200 px-4 py-3.5 text-sm cursor-pointer transition-colors">
            <Paperclip size={16} className="text-nx-goldlight" /> Attach a photo of the issue
            <input id="sr-file" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" className="hidden" onChange={pick} data-testid="sr-file-input" />
          </label>
        )}
      </div>
      <div className="sm:col-span-2">
        <button type="submit" disabled={sending} data-testid="sr-submit" className="w-full sm:w-auto bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold tracking-wide text-sm px-10 py-4 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60">
          {sending ? "Sending…" : "Raise Service Request"}
        </button>
      </div>
    </form>
  );
};
